"use client";

import { TypeBeforeAndAfterSection } from "@contentful/types";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@yardzen/ui/components/button";
import { H1 } from "@yardzen/ui/components/h1";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { notEmpty } from "@yardzen/js-util";
import { BeforeAndAfter } from "./BeforeAndAfter";
import { SelectorPills } from "./SelectorPills";

export function BeforeAndAfterSection({
  beforeAndAfterSection,
}: {
  beforeAndAfterSection: TypeBeforeAndAfterSection<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Pauses auto-rotation once the visitor picks a yard themselves.
  const [autoRotate, setAutoRotate] = useState(true);

  // Only entries that resolve both images can be shown: every fetch uses
  // `withoutUnresolvableLinks`, so an unpublished asset leaves `before` or
  // `after` undefined and `BeforeAndAfter` renders nothing. Rotating over the
  // raw list would park the section on an empty slot for ten seconds.
  const beforeAndAfterEntries = useMemo(
    () =>
      (beforeAndAfterSection.fields.beforeAfters ?? [])
        .filter(notEmpty)
        .filter(
          (entry) =>
            entry.fields.before?.fields.file?.url &&
            entry.fields.after?.fields.file?.url,
        ),
    [beforeAndAfterSection],
  );
  const entryCount = beforeAndAfterEntries.length;

  useEffect(() => {
    if (!autoRotate || entryCount < 2) return;
    const interval = window.setInterval(
      () => setCurrentIndex((i) => (i + 1) % entryCount),
      10_000,
    );
    return () => window.clearInterval(interval);
  }, [autoRotate, entryCount]);

  // Warm the next entry's images ahead of the rotation so the compare slider
  // never flashes empty while a fresh pair downloads.
  useEffect(() => {
    if (entryCount < 2) return;
    const next = beforeAndAfterEntries[(currentIndex + 1) % entryCount];
    [
      next?.fields.before?.fields.file?.url,
      next?.fields.after?.fields.file?.url,
    ].forEach((url) => {
      if (url) new window.Image().src = url;
    });
  }, [currentIndex, entryCount, beforeAndAfterEntries]);

  function handleClick(index: number) {
    setAutoRotate(false);
    setCurrentIndex(index);
  }

  function parsePath(url: string | undefined) {
    if (!url) return "/before-after";
    // Editors type either a full URL or a bare path. `new URL` throws on the
    // second, which took the whole section down rather than degrading.
    try {
      return new URL(url).pathname;
    } catch {
      return url.startsWith("/") ? url : `/${url}`;
    }
  }
  const beforeAndAfterCTA = beforeAndAfterSection.fields.buttonText;
  const beforeAndAfterLink = parsePath(beforeAndAfterSection.fields.url);
  const router = useRouter();

  return (
    entryCount > 0 && (
      <div className="md:px-8">
        <div className="text-brand-secondary-dark flex w-full flex-col items-center gap-6 overflow-hidden px-8 py-12 md:gap-8 md:px-20 md:py-16">
          {/* The heading belongs to the tabs under it, so the two sit in their
              own group with a tighter gap than the section's. */}
          <div className="flex w-full flex-col items-center gap-3">
            <H1 className="pb-0 text-center">Explore Before &amp; Afters</H1>
            <SelectorPills
              label="Before and after projects"
              selectedIndex={currentIndex}
              onSelect={handleClick}
              options={beforeAndAfterEntries.map((entry) => ({
                id: entry.sys.id,
                label: entry.fields.title,
              }))}
            />
          </div>
          {beforeAndAfterEntries[currentIndex] && (
            <div
              key={currentIndex}
              className="animate-fadeIn flex w-full flex-col items-center gap-6 md:gap-8"
            >
              <BeforeAndAfter
                beforeAndAfter={beforeAndAfterEntries[currentIndex]}
              />
            </div>
          )}
          <div className="flex w-full justify-center">
            <Button
              variant="secondary"
              endIcon={
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="text-brand-primary-dark"
                />
              }
              onClick={() => {
                router.push(`${beforeAndAfterLink}`);
              }}
            >
              {beforeAndAfterCTA}
            </Button>
          </div>
        </div>
      </div>
    )
  );
}
